import { useMemo } from 'react';
import { useResponsive, useMemoizedFn } from 'ahooks';

// 设备类型枚举
export type DeviceType = 'mobile' | 'tablet' | 'desktop';

// 检测策略枚举
export type DetectionStrategy = 'responsive' | 'userAgent' | 'width' | 'both';

// 配置选项接口
export interface UseDeviceTypeOptions {
  // 移动端断点，默认768px
  mobile_breakpoint?: number;
  // 平板断点，默认1024px
  tablet_breakpoint?: number;
  // 是否将平板视为移动设备，默认false
  include_tablet_as_mobile?: boolean;
  // 检测策略，默认'responsive'
  strategy?: DetectionStrategy;
  // 自定义响应式断点配置
  responsive_config?: { [key: string]: number; };
}

// 返回值接口
export interface DeviceInfo {
  is_mobile: boolean;
  is_tablet: boolean;
  is_desktop: boolean;
  device_type: DeviceType;
  screen_width: number;
  screen_height: number;
}

// UserAgent检测移动设备
const detect_mobile_by_user_agent = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  return !!navigator.userAgent.match(
    /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i
  );
};

// UserAgent检测平板设备
const detect_tablet_by_user_agent = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  return !!navigator.userAgent.match(
    /(iPad|Android(?!.*Mobile)|Tablet)/i
  );
};

// 默认响应式断点配置（基于Bootstrap标准）
const DEFAULT_RESPONSIVE_CONFIG = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
};

export const useDeviceType = (options: UseDeviceTypeOptions = {}): DeviceInfo => {
  const {
    mobile_breakpoint = 768,
    tablet_breakpoint = 1024,
    include_tablet_as_mobile = false,
    strategy = 'responsive',
    responsive_config = DEFAULT_RESPONSIVE_CONFIG,
  } = options;

  // 使用ahook的useResponsive
  const responsive = useResponsive();

  // 获取设备信息的缓存函数
  const get_device_info = useMemoizedFn((): DeviceInfo => {
    let is_mobile = false;
    let is_tablet = false;
    let is_desktop = false;

    // 获取屏幕尺寸
    const screen_width = typeof window !== 'undefined' ? window.innerWidth : 0;
    const screen_height = typeof window !== 'undefined' ? window.innerHeight : 0;

    switch (strategy) {
      case 'width':
        // 基于屏幕宽度检测
        if (screen_width < mobile_breakpoint) {
          is_mobile = true;
        } else if (screen_width < tablet_breakpoint) {
          is_tablet = true;
        } else {
          is_desktop = true;
        }
        break;

      case 'userAgent':
        // 基于UserAgent检测
        is_mobile = detect_mobile_by_user_agent();
        is_tablet = detect_tablet_by_user_agent();
        is_desktop = !is_mobile && !is_tablet;
        break;

      case 'responsive':
        // 基于ahook的useResponsive检测（默认方式）
        is_mobile = !responsive.sm;
        is_tablet = responsive.sm && !responsive.lg;
        is_desktop = responsive.lg;
        break;

      case 'both': {
        // 结合宽度和UserAgent检测
        const width_is_mobile = screen_width < mobile_breakpoint;
        const width_is_tablet = screen_width >= mobile_breakpoint && screen_width < tablet_breakpoint;
        const ua_is_mobile = detect_mobile_by_user_agent();
        const ua_is_tablet = detect_tablet_by_user_agent();

        is_mobile = width_is_mobile || ua_is_mobile;
        is_tablet = width_is_tablet || ua_is_tablet;
        is_desktop = !is_mobile && !is_tablet;
        break;
      }
    }

    // 如果设置了将平板视为移动设备
    if (include_tablet_as_mobile && is_tablet) {
      is_mobile = true;
      is_tablet = false;
    }

    // 确保只有一个设备类型为true
    if (is_mobile) {
      is_tablet = false;
      is_desktop = false;
    } else if (is_tablet) {
      is_desktop = false;
    } else {
      is_desktop = true;
    }

    // 确定设备类型
    let device_type: DeviceType = 'desktop';
    if (is_mobile) device_type = 'mobile';
    else if (is_tablet) device_type = 'tablet';

    return {
      is_mobile,
      is_tablet,
      is_desktop,
      device_type,
      screen_width,
      screen_height,
    };
  });

  // 使用useMemo缓存结果，依赖于相关状态
  const device_info = useMemo(() => {
    return get_device_info();
  }, [
    responsive.xs,
    responsive.sm,
    responsive.md,
    responsive.lg,
    responsive.xl,
    get_device_info
  ]);

  return device_info;
};

// 向后兼容的简化版本（保持原有API）
export const useDeviceTypeSimple = () => {
  const { is_mobile, is_tablet, is_desktop } = useDeviceType();

  return {
    isMobile: is_mobile,
    isPad: is_tablet,
    isPc: is_desktop
  };
};
